/**
 * PayCircle Credential Form
 *
 * Form for configuring Access PayCircle API credentials with:
 * - Username for authentication
 * - Password for authentication
 * - Optional custom base URL
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface PayCircleFormData {
  username: string;
  password: string;
  baseUrl: string;
}

interface PayCircleCredentialFormProps {
  formData: PayCircleFormData;
  onChange: (data: Partial<PayCircleFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; data?: any; details?: any } | null;
  testing?: boolean;
  onTest?: () => Promise<void>;
}

export function PayCircleCredentialForm({
  formData,
  onChange,
  isEditing = false,
}: PayCircleCredentialFormProps) {
  const handleChange = (field: keyof PayCircleFormData, value: string) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* Username */}
      <div className="space-y-2">
        <Label htmlFor="username" className="text-sm font-medium">
          Username <span className="text-red-500">*</span>
        </Label>
        <Input
          id="username"
          placeholder={isEditing ? "Leave unchanged or enter new username" : "Enter your PayCircle API username"}
          value={formData.username || ''}
          onChange={(e) => handleChange('username', e.target.value)}
          className="text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Your PayCircle API username (provided by Account Manager)
        </p>
      </div>

      {/* Password */}
      <div className="space-y-2">
        <Label htmlFor="password" className="text-sm font-medium">
          Password <span className="text-red-500">*</span>
        </Label>
        <Input
          id="password"
          type="password"
          placeholder={isEditing ? "Leave unchanged or enter new password" : "Enter your PayCircle API password"}
          value={formData.password || ''}
          onChange={(e) => handleChange('password', e.target.value)}
          autoComplete="new-password"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Your PayCircle API password
        </p>
      </div>

      {/* Base URL (Optional) */}
      <div className="space-y-2">
        <Label htmlFor="baseUrl" className="text-sm font-medium">
          Base URL <span className="text-muted-foreground text-xs">(Optional)</span>
        </Label>
        <Input
          id="baseUrl"
          placeholder="https://api.paycircle.co.uk"
          value={formData.baseUrl || ''}
          onChange={(e) => handleChange('baseUrl', e.target.value)}
          className="text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Leave default unless using a custom instance
        </p>
      </div>
    </div>
  );
}

export default PayCircleCredentialForm;
